import { useState } from 'react';
import { useDebounce } from 'react-use';

import { useSearchAllUsersWithTotalQuery, useSearchUsersForGalleryQuery } from 'src/hooks/__generated__/queries';
import type { User } from 'src/types/__generated__/graphql';
import { GalleryUserStatus } from 'src/types/__generated__/graphql';

type SelectableUser =
    | {
          type: 'user';
          value: User;
      }
    | {
          type: 'email';
          value: string;
      };

export type CollaboratorUser = {
    id: string;
    status: GalleryUserStatus;
    user: User;
};

type Response = {
    query: string;
    setQuery: (value: string) => void;
    queryIsEmpty: boolean;
    selectedUsers: SelectableUser[];
    setSelectedUsers: (users: SelectableUser[]) => void;
    users: CollaboratorUser[];
    loading: boolean;
    totalCount: number;
};

type Props = {
    skip?: boolean;
    limit?: number;
    galleryId?: string;
};

export const useSearchCollaborators = ({ skip, limit = 25, galleryId }: Props): Response => {
    const [query, setQuery] = useState('');
    const [selectedUsers, setSelectedUsers] = useState<SelectableUser[]>([]);
    const [debouncedQuery, setDebouncedQuery] = useState('');

    useDebounce(() => setDebouncedQuery(query), 500, [query]);

    const { data: allUsers, loading: loadingAllUsers } = useSearchAllUsersWithTotalQuery({
        variables: {
            offset: 0,
            limit,
            search: debouncedQuery.length > 0 ? debouncedQuery : null,
        },
        skip: Boolean(galleryId) || skip,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const { data: usersForGallery, loading: loadingUsersForGallery } = useSearchUsersForGalleryQuery({
        variables: {
            id: galleryId ?? '',
            searchString: debouncedQuery.length > 0 ? debouncedQuery : '',
            pageRequest: {
                offset: 0,
                limit,
            },
        },
        skip: !galleryId || skip,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const users =
        (galleryId
            ? usersForGallery?.gallery?.searchUsers.items
            : allUsers?.searchAllUsersWithTotal?.results?.map((user) => ({
                  status: GalleryUserStatus.Unaffiliated,
                  user,
                  id: user?.id,
              }))) ?? [];

    const totalCount =
        (galleryId ? usersForGallery?.gallery?.searchUsers.totalCount : allUsers?.searchAllUsersWithTotal?.total) ?? 0;

    return {
        query: debouncedQuery,
        setQuery: (value: string) => setQuery(value),
        queryIsEmpty: query.length === 0,
        selectedUsers,
        setSelectedUsers,
        users: users as CollaboratorUser[],
        loading: galleryId ? loadingUsersForGallery : loadingAllUsers,
        totalCount,
    };
};
